/**
 * Action buttons — the shared "a control the host named, rendered by us" primitive.
 *
 * A caller declares `{ id, label, icon, color }` and the component renders the
 * button and dispatches the id back. The host never writes button markup, which
 * is what keeps a row action in a table and a New button in a panel header
 * looking like the same product.
 *
 * This lived inside g-table, where the row-action renderer grew the icon map,
 * the semantic-tone gate and the "any fa- class passes through" rule. g-panel
 * needs all three for its header. Copying them would have started exactly the
 * second vocabulary that g-table's own comments warn about — the one that made
 * hand-drawn SVG spread from this component into g-card-grid. So it moves here
 * and both import it.
 *
 *   renderActionButton({ id: 'create', label: 'New', icon: 'plus', color: 'primary' },
 *                      { attr: 'data-panel-action', className: 'btn btn-sm', showLabel: true })
 */

export function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

// Font Awesome, like every icon in this stack — the app shell loads the kit.
// `fa-regular` throughout, matching the generated apps.
export const ACTION_ICONS = {
  edit: 'fa-regular fa-pen',
  delete: 'fa-regular fa-trash',
  view: 'fa-regular fa-eye',
  duplicate: 'fa-regular fa-copy',
  archive: 'fa-regular fa-box-archive',
  more: 'fa-regular fa-ellipsis',
  create: 'fa-regular fa-plus',
  plus: 'fa-regular fa-plus',
  add: 'fa-regular fa-plus',
  refresh: 'fa-regular fa-arrows-rotate',
  export: 'fa-regular fa-arrow-down-to-line',
  import: 'fa-regular fa-arrow-up-from-line',
  filter: 'fa-regular fa-filter',
  settings: 'fa-regular fa-gear',
};

// Semantic tokens only — an absolute colour stops reading after a theme switch,
// the same constraint rowStatus applies one level up.
export const ACTION_TONES = new Set([
  'primary', 'secondary', 'accent', 'info', 'success', 'warning', 'error', 'neutral',
]);

/** `'edit'` or `{ id, … }` → a filled-out spec, or null when there is no id. */
export function normalizeAction(a) {
  const spec = typeof a === 'string' ? { id: a } : (a || {});
  if (!spec.id) return null;
  return {
    id: String(spec.id),
    label: spec.label || spec.id.charAt(0).toUpperCase() + spec.id.slice(1),
    icon: spec.icon,
    color: spec.color,
  };
}

/**
 * The glyph for an action. The map is shorthand for the handful of actions that
 * recur, not a gate: a caller may name any Font Awesome class directly.
 */
export function actionIconClass(spec) {
  return ACTION_ICONS[spec.icon]
    || (typeof spec.icon === 'string' && spec.icon.startsWith('fa-') ? spec.icon : null)
    || ACTION_ICONS[spec.id]
    || ACTION_ICONS.more;
}

/**
 * One button.
 *
 * @param {Object} spec                 normalized action
 * @param {Object} opts
 * @param {string} opts.attr            data attribute carrying the id back to the click handler
 * @param {string} opts.className       base button classes
 * @param {boolean} [opts.showLabel]    render the label beside the icon (header buttons) or
 *                                      leave it to title/aria only (icon-square row buttons)
 * @param {boolean} [opts.tinted]       colour the TEXT (ghost row buttons) rather than
 *                                      the button itself (header buttons)
 */
export function renderActionButton(spec, { attr, className, showLabel = false, tinted = false }) {
  if (!spec) return '';
  const hasTone = ACTION_TONES.has(spec.color);
  // A row button is a ghost with tinted glyph; a header button is a solid
  // `btn-<tone>`. Delete stays red without being asked, in both.
  const tone = tinted
    ? (hasTone ? ` text-${spec.color}` : (spec.id === 'delete' ? ' text-error' : ''))
    : (hasTone ? ` btn-${spec.color}` : (spec.id === 'delete' ? ' btn-error btn-outline' : ' btn-ghost'));
  const icon = `<i class="${escapeHtml(actionIconClass(spec))}" aria-hidden="true"></i>`;
  const label = showLabel ? ` ${escapeHtml(spec.label)}` : '';
  return `<button type="button" class="${className}${tone}"`
    + ` ${attr}="${escapeHtml(spec.id)}"`
    + ` title="${escapeHtml(spec.label)}" aria-label="${escapeHtml(spec.label)}">`
    + `${icon}${label}</button>`;
}

/** Several, already filtered of the ones with no id. */
export function renderActionButtons(actions, opts) {
  return (Array.isArray(actions) ? actions : [])
    .map(normalizeAction)
    .filter(Boolean)
    .map((spec) => renderActionButton(spec, opts))
    .join('');
}
